import React, { useState, useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  Card,
  Grid,
  Button,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";


import { get, post, delNoResponse } from "utils/WebService";
import { toDateTime } from "utils/DateUtils";
import { ESCURO } from "assets/coresTema";

function MalaDiretaModelos() {
  const [modelos, setModelos] = useState([]);
  const [variaveis, setVariaveis] = useState([]);
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentModelo, setCurrentModelo] = useState({ id: null, nome: "", assunto: "", conteudoHtml: "" });
  const [lastFocusedField, setLastFocusedField] = useState("corpo");

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const quillRef = useRef(null);

  useEffect(() => {
    fetchModelos();
    fetchVariaveis();
  }, []);

  const fetchModelos = async () => {
    try {
      const res = await get("/mala-direta/modelos");
      setModelos(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchVariaveis = async () => {
    try {
      const res = await get("/mala-direta/variaveis");
      setVariaveis(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenEditor = (modelo = { id: null, nome: "", assunto: "", conteudoHtml: "" }) => {
    setCurrentModelo(modelo);
    setIsEditing(true);
  };

  const handleCloseEditor = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      await post("/mala-direta/modelos", {
        cvId: currentModelo.id,
        ccNome: currentModelo.nome,
        ccAssunto: currentModelo.assunto,
        ccConteudoHtml: currentModelo.conteudoHtml,
      });
      fetchModelos();
      handleCloseEditor();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar modelo");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente excluir este modelo?")) {
      try {
        await delNoResponse(`/mala-direta/modelos/${id}`);
        fetchModelos();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const insertVariable = (varId) => {
    if (lastFocusedField === "assunto") {
      setCurrentModelo(prev => ({
        ...prev,
        assunto: (prev.assunto || "") + varId
      }));
    } else {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        const cursorPosition = editor.getSelection()?.index || 0;
        editor.insertText(cursorPosition, varId);
        editor.setSelection(cursorPosition + varId.length);
      }
    }
  };

  const handleResizeImage = () => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    
    // Find selected image
    const selection = editor.getSelection();
    if (!selection) {
      alert("Clique em uma imagem dentro do texto para selecioná-la primeiro.");
      return;
    }
    
    const [leaf] = editor.getLeaf(selection.index);
    if (leaf && leaf.domNode && leaf.domNode.tagName === 'IMG') {
      const currentWidth = leaf.domNode.getAttribute('width') || leaf.domNode.width || "";
      const width = prompt("Informe a largura da imagem em pixels (exemplo: 600 ou 800):", currentWidth);
      if (width) {
        leaf.domNode.setAttribute('width', width);
        // Force quill to register the change
        editor.update();
      }
    } else {
      alert("Nenhuma imagem selecionada. Clique diretamente na imagem para selecioná-la e tente novamente.");
    }
  };

  const modules = {
    toolbar: [
      [{ font: [] }, { size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["image", "clean"],
    ],
  };

  if (isEditing) {
    return (
      <Grid container display="flex" justifyContent="center">
        <Grid item xs={12} md={12}>
          <Card>
            <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5">{currentModelo.id ? "Editar Modelo" : "Novo Modelo"}</MDTypography>
              <MDButton color="light" onClick={handleCloseEditor}>
                Voltar
              </MDButton>
            </MDBox>
            <Divider />
            <MDBox p={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <MDInput
                    label="Nome do Modelo"
                    fullWidth
                    value={currentModelo.nome}
                    onChange={(e) => setCurrentModelo({ ...currentModelo, nome: e.target.value })}
                    margin="normal"
                  />
                  <MDInput
                    label="Assunto do E-mail (Você pode usar variáveis aqui)"
                    fullWidth
                    value={currentModelo.assunto || ""}
                    onChange={(e) => setCurrentModelo({ ...currentModelo, assunto: e.target.value })}
                    onFocus={() => setLastFocusedField("assunto")}
                    margin="normal"
                  />
                  
                  <MDBox mt={2} mb={2}>
                    <MDTypography variant="subtitle2" color="text" gutterBottom>
                      Inserir Variável Dinâmica:
                    </MDTypography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <MDSelect
                          options={variaveis}
                          getOptionLabel={(v) => `${v.nome} (${v.id})`}
                          getOptionValue={(v) => v.id}
                          onChange={(option) => {
                            if (option) insertVariable(option.id);
                          }}
                          placeholder="Selecione a variável para inserir no texto..."
                          isClearable={false}
                          value={null}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text">
                          Selecione uma variável na lista para inseri-la automaticamente no campo Assunto ou no Corpo do E-mail (onde você clicou por último).
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                    <MDTypography variant="subtitle2" color="text">
                      Corpo do E-mail (Copie do Word e cole aqui)
                    </MDTypography>
                    <MDButton variant="contained" color="info" size="small" onClick={handleResizeImage}>
                      Ajustar Tamanho da Imagem Selecionada
                    </MDButton>
                  </MDBox>
                  <MDBox sx={{ 
                    maxWidth: "1000px",
                    margin: "0 auto",
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                    "& .ql-editor": { 
                      color: darkMode ? "#ffffff" : "#333333",
                    },
                    "& .ql-toolbar": {
                      backgroundColor: darkMode ? ESCURO.cartao : "#f1f1f1",
                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2) !important" : "#ccc"
                    },
                    "& .ql-container": {
                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2) !important" : "#ccc"
                    },
                    "& .ql-stroke": {
                      stroke: darkMode ? "#ffffff !important" : "#444444 !important"
                    },
                    "& .ql-fill": {
                      fill: darkMode ? "#ffffff !important" : "#444444 !important"
                    },
                    "& .ql-picker": {
                      color: darkMode ? "#ffffff !important" : "#444444 !important"
                    },
                    "& .ql-picker-options": {
                      backgroundColor: darkMode ? `${ESCURO.cartao} !important` : "#ffffff !important",
                      borderColor: darkMode ? "rgba(255, 255, 255, 0.2) !important" : "#ccc !important"
                    }
                  }}>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      modules={modules}
                      value={currentModelo.conteudoHtml}
                      onChange={(val) => setCurrentModelo({ ...currentModelo, conteudoHtml: val })}
                      onFocus={() => setLastFocusedField("corpo")}
                      style={{ height: "600px", marginBottom: "0px" }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            <Divider />
            <MDBox p={2} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton onClick={handleCloseEditor} color="light">
                Cancelar
              </MDButton>
              <MDButton onClick={handleSave} color="info" variant="contained">
                Salvar
              </MDButton>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container display="flex" justifyContent="center">
      <Grid item xs={12} md={12}>
        <Card>
          <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">Modelos de Mala Direta</MDTypography>
            <MDButton
              variant="contained"
              color="info"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEditor()}
            >
              Novo Modelo
            </MDButton>
          </MDBox>
          <Divider />
          <MDBox p={3}>
            <List>
              {modelos.map((mod) => (
                <ListItem key={mod.cvId} divider>
                  <ListItemText 
                    primary={<MDTypography variant="button" color="text" fontWeight="medium">{mod.ccNome}</MDTypography>} 
                    secondary={<MDTypography variant="caption" color="text">{`Assunto: ${mod.ccAssunto || "Sem assunto"} | Criado em: ${mod.cdCriacao ? toDateTime(mod.cdCriacao) : ""}`}</MDTypography>} 
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="info" onClick={() => handleOpenEditor({ id: mod.cvId, nome: mod.ccNome, assunto: mod.ccAssunto, conteudoHtml: mod.ccConteudoHtml })}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" color="error" onClick={() => handleDelete(mod.cvId)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {modelos.length === 0 && <MDTypography variant="button" color="text" fontWeight="regular">Nenhum modelo cadastrado.</MDTypography>}
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
}

export default MalaDiretaModelos;
